import { FC, ReactNode, useState } from 'react';
import { LayoutGrid, Lock, Plus, Trash2, X } from 'lucide-react';
import { Modal, cn } from '@crisellevaldez/speculo-ui';
import { getLockedWidgets } from '../../stores/useWidgetPreferences';
import { useFlywheelEnabled } from '../../stores/useOrganizationStore';

export interface AvailableWidget {
  id: string;
  label: string;
  description?: string;
  icon: ReactNode;
}

interface AddWidgetCardProps {
  coreWidgets: AvailableWidget[];
  libraryWidgets: AvailableWidget[];
  activeWidgetIds: Set<string>;
  onAdd: (id: string) => void;
  onRemoveFromDashboard: (id: string) => void;
  onPermanentDelete: (id: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const AddWidgetCard: FC<AddWidgetCardProps> = ({
  coreWidgets,
  libraryWidgets,
  activeWidgetIds,
  onAdd,
  onRemoveFromDashboard,
  onPermanentDelete,
  isOpen: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const flywheelEnabled = useFlywheelEnabled();
  const lockedWidgets = getLockedWidgets(flywheelEnabled);

  const toggle = (id: string) => {
    if (lockedWidgets.includes(id)) return; // locked widgets can't be toggled
    activeWidgetIds.has(id) ? onRemoveFromDashboard(id) : onAdd(id);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-200 cursor-pointer border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-500 dark:border-amber-500 dark:text-amber-400 dark:bg-amber-950/30 dark:hover:bg-amber-950/50"
      >
        <LayoutGrid className="w-3.5 h-3.5" />
        Widget Library
      </button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <Modal.Header onClose={() => setIsOpen(false)}>
          <h3 className="text-lg font-bold">Widget Library</h3>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-5">
            <Section label="System">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...coreWidgets].sort((a, b) => {
                  const aLocked = lockedWidgets.includes(a.id) ? 0 : 1;
                  const bLocked = lockedWidgets.includes(b.id) ? 0 : 1;
                  return aLocked - bLocked;
                }).map((w) => (
                  <WidgetBox
                    key={w.id}
                    widget={w}
                    active={activeWidgetIds.has(w.id)}
                    locked={lockedWidgets.includes(w.id)}
                    onToggle={() => toggle(w.id)}
                  />
                ))}
              </div>
            </Section>

            <div className="border-t border-border" />

            <Section label="Remi">
              {libraryWidgets.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {libraryWidgets.map((w) => (
                    <WidgetBox
                      key={w.id}
                      widget={w}
                      active={activeWidgetIds.has(w.id)}
                      onToggle={() => toggle(w.id)}
                      onDelete={() => {
                        onRemoveFromDashboard(w.id);
                        onPermanentDelete(w.id);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-3">
                  Ask Remi to create insights and pin them here.
                </p>
              )}
            </Section>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

/* ── internal helpers ────────────────────────── */

const Section: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
      {label}
    </p>
    {children}
  </div>
);

const WidgetBox: FC<{
  widget: AvailableWidget;
  active: boolean;
  locked?: boolean;
  onToggle: () => void;
  onDelete?: () => void;
}> = ({ widget, active, locked, onToggle, onDelete }) => (
  <div
    className={cn(
      'relative rounded-lg border p-4 transition-colors',
      active ? 'border-green-300 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10' : 'border-border'
    )}
  >
    <div className="flex items-center gap-1.5">
      <p className="text-base font-medium">{widget.label}</p>
      {locked && <Lock className="w-3 h-3 text-muted-foreground" />}
    </div>
    {widget.description && (
      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{widget.description}</p>
    )}
    <div className="flex items-center gap-2 mt-3">
      {locked ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
          <Lock className="w-3 h-3" />
          Always On
        </span>
      ) : (
        <button
          onClick={onToggle}
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer',
            active
              ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50'
              : 'bg-primary text-primary-foreground hover:opacity-90'
          )}
        >
          {active ? (
            <>
              <X className="w-3 h-3" />
              Remove
            </>
          ) : (
            <>
              <Plus className="w-3 h-3" />
              Add
            </>
          )}
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className="p-1.5 rounded-full text-muted-foreground hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/30 transition-colors cursor-pointer ml-auto"
          title="Delete permanently"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);
